import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Switch } from "../ui/switch.js";
import {
  AGENT_TRUST_COPY,
  AGENT_TRUST_READ_ONLY,
  AGENT_TRUST_TITLE,
} from "../lib/agentSourcesCopy.js";
import { fetchWorkspaceRepos, WorkspaceReposError } from "../lib/workspaceRepos.js";
import { ChapterCard, ChapterLede, ChapterSection } from "./chapter.js";
import { useRepoAgentTrust } from "./useRepoAgentTrust.js";

export const REPO_SKILLS_SECTION = "Repository skills";
export const REPO_SKILLS_NONE = "No repositories are connected yet.";
export const REPO_SKILLS_LOAD_FAILED = "The repositories could not be read just now.";

interface RepoRow {
  id: string;
  fullName: string;
}

export type FetchRepos = () => Promise<RepoRow[]>;

/** The rows the list shows, in `fetchWorkspaceRepos`'s order: id and name. */
export async function fetchRepos(): Promise<RepoRow[]> {
  return (await fetchWorkspaceRepos()).map(({ id, fullName }) => ({ id, fullName }));
}

function RepoTrustRow({ repo }: { repo: RepoRow }) {
  const { trust, busy, error, setTrusted } = useRepoAgentTrust(repo.id);
  return (
    <li className="flex items-center justify-between gap-3 py-2" data-testid="repo-skills-row">
      <span className="min-w-0 text-sm">
        <span className="font-mono text-[var(--text-primary)]">{repo.fullName}</span>
        {error && (
          <span role="alert" className="block text-xs text-[var(--error)]">
            {error}
          </span>
        )}
      </span>
      {/* No switch until the read settles, so an off repository never shows
          on; a failed read shows the default, disabled. */}
      {(trust || error) && (
        <Switch
          aria-label={`${AGENT_TRUST_TITLE}: ${repo.fullName}`}
          title={AGENT_TRUST_COPY}
          checked={trust?.trusted ?? true}
          disabled={!trust || !trust.canEdit || busy}
          onCheckedChange={(next) => void setTrusted(next)}
        />
      )}
    </li>
  );
}

/**
 * Settings, Agent runs: every connected repository with its "Agents use this
 * repository's skills and rules" switch (YAG-784; on by default since
 * YAG-1006), so the answer to "where do I switch a repository's skills off"
 * is one list. The same switch sits on the repository's sheet under
 * Connections and on a Team's Works on list; all three read and write
 * `/api/repos/:id/agent-trust`.
 */
export function RepoSkillsList({ fetchRepos: load = fetchRepos }: { fetchRepos?: FetchRepos }) {
  const [repos, setRepos] = useState<RepoRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const rows = await load();
        if (live) setRepos(rows);
      } catch (err) {
        const status = err instanceof WorkspaceReposError ? ` (${err.status})` : "";
        console.warn(
          `Agent runs: the repositories read failed${status}; the list stays empty.`,
          err,
        );
        if (live) {
          setRepos([]);
          setError(REPO_SKILLS_LOAD_FAILED);
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [load]);

  return (
    <div data-testid="repo-skills-list">
      <ChapterSection label={REPO_SKILLS_SECTION} />
      <ChapterLede>
        {AGENT_TRUST_COPY} {AGENT_TRUST_READ_ONLY}
      </ChapterLede>
      <ChapterCard className="px-4 py-2">
        {error ? (
          <p role="alert" className="m-0 py-2 text-sm text-[var(--error)]">
            {error}
          </p>
        ) : repos === null ? (
          <p className="m-0 py-2 text-sm text-[var(--text-tertiary)]">Reading</p>
        ) : repos.length === 0 ? (
          <p className="m-0 py-2 text-sm text-[var(--text-secondary)]">
            {REPO_SKILLS_NONE}{" "}
            <Link to="/services/repositories" className="underline text-[var(--text-primary)]">
              Connect one under Connections
            </Link>
            .
          </p>
        ) : (
          <ul className="m-0 flex list-none flex-col divide-y divide-[var(--border-subtle)] p-0">
            {repos.map((repo) => (
              <RepoTrustRow key={repo.id} repo={repo} />
            ))}
          </ul>
        )}
      </ChapterCard>
    </div>
  );
}
